import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ERoutesPrivate } from 'common/models/routesModel';
import { isReferralRewardPopupShown, markReferralRewardPopupShown } from 'common/hooks/RewardsPopup.hooks';
import referralRewardGift from 'app/assets/images/referralRewardGift.png';
import { ReactComponent as BitecoinIcon } from 'app/assets/images/bitecoinIcon.svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getReferralSummary, markReferralRewardSeen } from 'app/store/reducers/referral.reducer';
import { getCustomerModel } from 'app/store/reducers/customer.reducer';

/**
 * MB-2753 + Figma 14527:18189: dopamine-hit popup shown once after the referrer earns
 * referral Bitecoins. Trigger is `customerModel.hasUnseenReferralReward` (from /auth/me).
 * The reward is marked seen (POST /customers/me/referral/seen) the moment the popup is
 * SHOWN — not on close — so it never re-appears (F5 included) even if left undismissed.
 * MB-2754: gated on `ui.isRewardsPopupResolved` so it never shows before/over RewardsPopup —
 * it only opens once RewardsPopup is resolved (not eligible, or manually closed).
 */
export const ReferralRewardPopup: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { customerModel } = useAppSelector((state) => state.customer);
  // MB-2754: RewardsPopup must be shown (and resolved) first — this popup waits for it so the
  // two never overlap and always appear in a fixed order.
  const { isRewardsPopupResolved } = useAppSelector((state) => state.ui);
  const [visible, setVisible] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  // Snapshot at show time: the seen-ack immediately zeroes the counter on the BE and the
  // customer-model refetch would wipe it from Redux while the popup is still open.
  const [earnedAmount, setEarnedAmount] = useState(0);
  const userId = customerModel?.userId;

  // The component is mounted app-wide and never unmounts: reset the in-session ack when the
  // account changes (logout → login), otherwise the popup stays suppressed until a full reload.
  useEffect(() => {
    setAcknowledged(false);
    setVisible(false);
  }, [userId]);

  useEffect(() => {
    if (customerModel?.hasUnseenReferralReward && !acknowledged && userId && isRewardsPopupResolved) {
      const amount = customerModel.unseenReferralBitecoins || 0;

      // F5-guard: an instant reload can kill the seen-POST in flight, leaving the BE flag raised —
      // without this per-tab mark the popup would re-appear on every reload until the reset lands.
      if (!isReferralRewardPopupShown(userId, amount)) {
        setEarnedAmount(amount);
        setVisible(true);
        // Written synchronously at show time so it survives an immediate F5.
        markReferralRewardPopupShown(userId, amount);
      }

      // `acknowledged` guards against a second fire while the seen/refetch round-trip is in flight.
      setAcknowledged(true);
      // Fired even when the popup itself is suppressed by the session mark: if a previous reload
      // killed the seen-POST, this retry converges the BE flag (single source of truth).
      (async () => {
        await dispatch(markReferralRewardSeen());
        // Refresh the summary so the profile block reflects the new totals immediately.
        dispatch(getReferralSummary());
        // MB-2753 review #5: re-sync the customer model so `hasUnseenReferralReward` clears in
        // Redux within the session (otherwise it stays `true` until auth-me is re-fetched).
        dispatch(getCustomerModel(userId));
      })();
    }
  }, [customerModel?.hasUnseenReferralReward, acknowledged, userId, isRewardsPopupResolved]);

  const onClose = () => setVisible(false);

  const onRedeem = () => {
    setVisible(false);
    navigate(ERoutesPrivate.Bitecoin);
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={364}
      className="referral-reward-popup"
      closeIcon={<span className="referral-reward-popup__close">✕</span>}
    >
      <div className="referral-reward-popup__title">
        Someone just used your code!
        <br />
        You earned {earnedAmount} Bitecoins
      </div>
      <div className="referral-reward-popup__amount">
        <span>+{earnedAmount}</span>
        <BitecoinIcon />
      </div>
      <div className="referral-reward-popup__gift-wrapper">
        <img className="referral-reward-popup__gift" src={referralRewardGift} alt="gift" />
      </div>
      <Button type="primary" block size="large" className="btn" onClick={onRedeem}>
        Redeem Now
      </Button>
    </Modal>
  );
};
